/**
 * Strip the `$...$` from a field that is entirely prose wrapped in dollars.
 *
 * ESAT has options stored as `$6 alpha decays and 6 beta-minus decays$`. MathJax
 * reads them as inline maths and renders the words italic and run together,
 * with both dollars gone. Escaping is not the answer here, because that would
 * show two literal dollar signs around an answer option.
 *
 * The disabled LATEX_TEXT_MODIFIERS_ENABLED path did unwrap these, but its test
 * for "looks like maths" only accepted LaTeX commands, `_`, `^` and digit
 * inequalities, so it also unwrapped `$b = -4a$` and `$3:1$` and stopped TMUA
 * algebra typesetting. That is why it was turned off in #418.
 *
 * This asks the opposite question: is the content English? Three or more words
 * of three or more letters, and no LaTeX control sequence, `^` or `_` anywhere.
 * An algebraic expression has no such words, so every TMUA and ESAT maths span
 * is left exactly as it is.
 */
const LATEX_HINT = /\\[a-zA-Z]+|[\^_]/;
const WORD = /\b[A-Za-z]{3,}\b/g;
const MIN_WORDS = 3;

export function unwrapProseWrappedInDollars(text) {
  if (typeof text !== 'string' || !text.includes('$')) return text || '';

  const first = text.indexOf('$');
  const last = text.lastIndexOf('$');
  if (first === last) return text;

  // The dollars must be the only two, and must open and close the whole field.
  if ((text.match(/(?<!\\)\$/g) || []).length !== 2) return text;
  if (text.slice(0, first).trim() !== '') return text;
  if (text.slice(last + 1).trim() !== '') return text;

  const inner = text.slice(first + 1, last);
  if (LATEX_HINT.test(inner)) return text;
  if ((inner.match(WORD) || []).length < MIN_WORDS) return text;

  return text.slice(0, first) + inner + text.slice(last + 1);
}
